import React, { useCallback, useEffect, useState } from 'react';
import {
  Popover,
  makeStyles,
  TextField,
  List,
  InputAdornment,
  ListItemText,
  ListItem,
  Typography
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import * as userActions from 'app/auth/store/actions';
import { useTranslation } from 'react-i18next';
import { StoreIcon } from '../../icons/StoreIcon';
import useAxios from 'axios-hooks';

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  input: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 32,
    marginBottom: theme.spacing(1)
  },
  listItemContent: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 0
    }
  },
  title: {
    fontWeight: theme.typography.fontWeightBold
  },
  item: {
    fontSize: 18,
    margin: 0
  }
}));

const HairdresserPopover = ({ onClose, ...other }) => {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState('');
  const user = useSelector(({ auth }) => auth.user);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (other.open) {
      setUrl('/hairdressers');
    } else {
      setUrl(null);
    }
  }, [other.open]);

  const [{ data: hairdressersMe, loading, error }] = useAxios(url ? { url, method: 'GET' } : null, {
    manual: !url,
  });

  const dispatch = useDispatch();
  const classes = useStyles();

  const handleChange = useCallback(
    event => {
      setValue(event.target.value);
    },
    [setValue]
  );

  const handleClick = useCallback(
    (_, hairdresser) => {
      dispatch(
        userActions.setUserData({
          ...user,
          hairdresserSelected: hairdresser
        })
      );

      onClose();
    },
    [dispatch, onClose]
  );

  const getLabel = hairdresser => {
    return `${
      hairdresser.code
        ? `${hairdresser.code} - ${hairdresser.name}`
        : `${hairdresser.name}`
    }`;
  };

  const initHairdressers = React.useMemo(() => {
    const { hairdresserSelected } = user;
    const hairdressers = [];

    hairdressersMe?.forEach(hairdresserMe => {
      // Must not retrieve the hairdresser selected by the current user
      if (!hairdresserSelected || hairdresserMe.id !== hairdresserSelected.id) {
        const index = hairdressers.findIndex(
          newHairdresser => newHairdresser.brand_id === hairdresserMe.brand_id
        );

        if (index !== -1) {
          hairdressers[index].items.push(hairdresserMe);
        } else {
          hairdressers.push({
            title: hairdresserMe.brands?.name,
            brand_id: hairdresserMe.brand_id,
            items: [hairdresserMe]
          });
        }
      }
    });

    return hairdressers;
  }, [user, hairdressersMe]);

  const hairdressers = React.useMemo(() => {
    const filteredHairdressers = [];

    initHairdressers.forEach(hairdresser => {
      const items = hairdresser.items.filter(item =>
        getLabel(item).toLocaleLowerCase().includes(value.toLocaleLowerCase())
      );

      if (items.length > 0) {
        filteredHairdressers.push({
          ...hairdresser,
          items
        });
      }
    });

    return filteredHairdressers;
  }, [user, value, hairdressersMe]);

  return (
    <Popover
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      {...other}
    >
      <div className={classes.paper}>
        <TextField
          value={value}
          onChange={handleChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StoreIcon color="primary" />
              </InputAdornment>
            ),
            classes: {
              root: classes.input
            }
          }}
          placeholder={t('hairdressers.which_hairdressing_salon')}
        />
        <List>
          {hairdressers.map((hairdresser, keyHairdresser) => (
            <div
              className={classes.listItemContent}
              key={`HairdresserPopover-List-${keyHairdresser}`}
            >
              <Typography className={classes.title} gutterBottom>
                {hairdresser.title}
              </Typography>
              {hairdresser.items.map((item, keyItem) => (
                <ListItem
                  key={`HairdresserPopover-ListItem-${keyItem}`}
                  button
                  onClick={event => handleClick(event, item)}
                >
                  <ListItemText
                    primary={getLabel(item)}
                    className={classes.item}
                  />
                </ListItem>
              ))}
            </div>
          ))}
        </List>
      </div>
    </Popover>
  );
};

export default HairdresserPopover;
